/**
 * 서버가 센 숫자(`analytics.pivot`) → 표·그림 부품이 읽는 모양.
 *
 * 피벗 탭과 대시보드 위젯이 **같은 변환**을 써야 같은 숫자가 나온다.
 * ⚠️ 열 축을 안 골랐으면 한 칸(`전체`)으로 접는다 — 부품이 그 이름으로 그린다.
 */

import type { PivotCell, PivotTable } from 'src/utils/pivot/pivot';
import type { MomentAnalyticsPivotResponse } from 'src/types/moments/moment-ops';

import { ALL_LABEL } from 'src/utils/pivot/pivot';

export function toPivotTable(pivot: MomentAnalyticsPivotResponse): PivotTable {
  const cells: Record<string, Record<string, PivotCell>> = {};
  const rowTotals: Record<string, PivotCell> = {};
  const columnTotals: Record<string, PivotCell> = {};
  const single = pivot.columns.length === 0;

  pivot.rows.forEach((row) => {
    cells[row.key] = single
      ? { [ALL_LABEL]: row.total }
      : Object.fromEntries(row.cells.map((cell) => [cell.column, { value: cell.value, count: cell.count }]));
    rowTotals[row.key] = row.total;
  });

  pivot.columnTotals.forEach((column) => {
    columnTotals[column.column] = { value: column.value, count: column.count };
  });
  if (single) columnTotals[ALL_LABEL] = pivot.total;

  return {
    rows: pivot.rows.map((row) => row.key),
    columns: single ? [ALL_LABEL] : pivot.columns,
    cells,
    rowTotals,
    columnTotals,
    total: pivot.total,
  };
}

/**
 * 열의 뒤쪽 `count` 칸만 남긴 표 — 날짜 열을 최근 몇 칸으로 줄일 때. 줄 합계·전체는 남은 칸으로 다시 센다.
 * ⚠️ 열 합계는 받은 값 그대로 둔다(남은 열의 합계는 그 열 안에서 이미 맞다).
 */
export function lastColumns(table: PivotTable, count: number): PivotTable {
  const zero: PivotCell = { value: 0, count: 0 };
  const columns = table.columns.slice(-count);
  const cells = Object.fromEntries(
    table.rows.map((row) => [row, Object.fromEntries(columns.map((column) => [column, table.cells[row]?.[column] ?? zero]))]),
  );
  const sum = (picks: PivotCell[]): PivotCell => picks.reduce((acc, cell) => ({ value: acc.value + cell.value, count: acc.count + cell.count }), zero);
  const rowTotals = Object.fromEntries(table.rows.map((row) => [row, sum(columns.map((column) => cells[row][column]))]));
  return {
    rows: table.rows,
    columns,
    cells,
    rowTotals,
    columnTotals: Object.fromEntries(columns.map((column) => [column, table.columnTotals[column] ?? zero])),
    total: sum(Object.values(rowTotals)),
  };
}
